import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Trophy, Award, Star, Flame, Target, Crown, Medal, Zap,
  TrendingUp, Users, Clock, BookOpen, Heart, Shield, Gift,
  ChevronUp, ChevronRight
} from "lucide-react";

const BADGE_ICONS = {
  footprints: Trophy,
  zap: Zap,
  "book-open": BookOpen,
  "graduation-cap": Award,
  flame: Flame,
  award: Award,
  trophy: Trophy,
  star: Star,
  crown: Crown,
  "shield-check": Shield,
  coins: Gift,
  gem: Star,
  users: Users,
  clock: Clock,
  sun: Star,
  moon: Star,
  heart: Heart,
  "message-circle": Users,
  medal: Medal
};

const RARITY_COLORS = {
  common: "bg-gray-100 text-gray-700 border-gray-300",
  uncommon: "bg-green-100 text-green-700 border-green-300",
  rare: "bg-blue-100 text-blue-700 border-blue-300",
  epic: "bg-purple-100 text-purple-700 border-purple-300",
  legendary: "bg-yellow-100 text-yellow-700 border-yellow-300"
};

export default function GamificationDashboard({ userId }) {
  const [stats, setStats] = useState(null);
  const [badges, setBadges] = useState({ earned: [], unearned: [] });
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState("all_time");

  useEffect(() => {
    if (userId) {
      loadGamificationData();
    }
  }, [userId, timeframe]);

  const loadGamificationData = async () => {
    setLoading(true);
    try {
      const [statsRes, badgesRes, leaderboardRes] = await Promise.all([
        api.get(`/phase24/gamification/stats/${userId}`),
        api.get(`/phase24/gamification/badges/${userId}`),
        api.get(`/phase24/gamification/leaderboard?timeframe=${timeframe}&limit=10`)
      ]);
      
      setStats(statsRes.data);
      setBadges(badgesRes.data);
      setLeaderboard(leaderboardRes.data.leaderboard || []);
    } catch (error) {
      console.error("Failed to load gamification data:", error);
      toast.error("Failed to load gamification data");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="gamification-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const level = stats?.level?.current || { level: 1, name: "Newcomer" };
  const progress = stats?.level?.progress || 0;
  const pointsToNext = stats?.level?.points_to_next || 100;

  return (
    <div className="space-y-6" data-testid="gamification-dashboard">
      {/* Stats Header */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-amber-100">
                <Trophy className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-amber-700">{stats?.total_points || 0}</p>
                <p className="text-sm text-amber-600">Total Points</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-orange-50 to-red-50 border-orange-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-orange-100">
                <Flame className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-orange-700">{stats?.current_streak || 0}</p>
                <p className="text-sm text-orange-600">Day Streak</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-indigo-50 border-purple-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-purple-100">
                <Award className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-purple-700">{badges.earned?.length || 0}</p>
                <p className="text-sm text-purple-600">Badges Earned</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-200">
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="p-3 rounded-xl bg-emerald-100">
                <Target className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-emerald-700">{stats?.courses_completed || 0}</p>
                <p className="text-sm text-emerald-600">Courses Done</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Level Progress */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold text-2xl">
                {level.level}
              </div>
              <div>
                <h3 className="text-xl font-bold">{level.name}</h3>
                <p className="text-muted-foreground">Level {level.level}</p>
              </div>
            </div>
            {stats?.level?.next_level && (
              <div className="text-right">
                <p className="text-sm text-muted-foreground">Next Level</p>
                <p className="font-semibold">{stats.level.next_level.name}</p>
                <p className="text-xs text-muted-foreground">{pointsToNext} points to go</p>
              </div>
            )}
          </div>
          <Progress value={progress} className="h-3" />
          <div className="flex justify-between mt-2 text-xs text-muted-foreground">
            <span>{stats?.total_points || 0} XP</span>
            <span>{stats?.level?.next_level?.min_points || 100} XP</span>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="badges" className="space-y-4">
        <TabsList>
          <TabsTrigger value="badges" data-testid="badges-tab">
            <Award className="w-4 h-4 mr-2" />Badges
          </TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="leaderboard-tab">
            <Trophy className="w-4 h-4 mr-2" />Leaderboard
          </TabsTrigger>
          <TabsTrigger value="progress" data-testid="progress-tab">
            <TrendingUp className="w-4 h-4 mr-2" />Progress
          </TabsTrigger>
        </TabsList>

        {/* Badges Tab */}
        <TabsContent value="badges">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Award className="w-5 h-5" />
                Your Badges ({badges.earned?.length || 0}/{badges.total_available || 0})
              </CardTitle>
              <CardDescription>Collect badges by completing courses and maintaining streaks</CardDescription>
            </CardHeader>
            <CardContent>
              {/* Earned Badges */}
              <div className="mb-6">
                <h4 className="font-semibold mb-3 flex items-center gap-2">
                  <Star className="w-4 h-4 text-yellow-500" /> Earned Badges
                </h4>
                {badges.earned?.length === 0 ? (
                  <p className="text-muted-foreground text-center py-8">
                    Complete courses and maintain streaks to earn badges!
                  </p>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                    {badges.earned?.map((badge) => {
                      const IconComponent = BADGE_ICONS[badge.icon] || Trophy;
                      return (
                        <div
                          key={badge.id}
                          className={`p-4 rounded-lg border-2 text-center transition-transform hover:scale-105 ${RARITY_COLORS[badge.rarity]}`}
                          data-testid={`badge-${badge.id}`}
                        >
                          <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-white/50 flex items-center justify-center">
                            <IconComponent className="w-6 h-6" />
                          </div>
                          <p className="font-semibold text-sm">{badge.name}</p>
                          <p className="text-xs opacity-75">{badge.points} pts</p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Unearned Badges */}
              <div>
                <h4 className="font-semibold mb-3 text-muted-foreground">Badges to Earn</h4>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
                  {badges.unearned?.slice(0, 10).map((badge) => {
                    const IconComponent = BADGE_ICONS[badge.icon] || Trophy;
                    return (
                      <div
                        key={badge.id}
                        className="p-4 rounded-lg border-2 border-dashed border-gray-200 text-center opacity-50"
                      >
                        <div className="w-12 h-12 mx-auto mb-2 rounded-full bg-gray-100 flex items-center justify-center">
                          <IconComponent className="w-6 h-6 text-gray-400" />
                        </div>
                        <p className="font-semibold text-sm text-gray-500">{badge.name}</p>
                        <p className="text-xs text-gray-400">{badge.description}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Leaderboard Tab */}
        <TabsContent value="leaderboard">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Trophy className="w-5 h-5" /> Leaderboard
                  </CardTitle>
                  <CardDescription>See how you rank against your colleagues</CardDescription>
                </div>
                <div className="flex gap-2">
                  {["all_time", "monthly", "weekly"].map((tf) => (
                    <Button
                      key={tf}
                      variant={timeframe === tf ? "default" : "outline"}
                      size="sm"
                      onClick={() => setTimeframe(tf)}
                    >
                      {tf === "all_time" ? "All Time" : tf.charAt(0).toUpperCase() + tf.slice(1)}
                    </Button>
                  ))}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {leaderboard.map((entry, index) => (
                  <div
                    key={entry.user_id}
                    className={`flex items-center justify-between p-4 rounded-lg ${
                      index === 0 ? "bg-yellow-50 border-2 border-yellow-200" :
                      index === 1 ? "bg-gray-50 border-2 border-gray-200" :
                      index === 2 ? "bg-orange-50 border-2 border-orange-200" :
                      "bg-slate-50"
                    } ${entry.user_id === userId ? "ring-2 ring-primary" : ""}`}
                    data-testid={`leaderboard-entry-${index}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                        index === 0 ? "bg-yellow-200 text-yellow-800" :
                        index === 1 ? "bg-gray-200 text-gray-800" :
                        index === 2 ? "bg-orange-200 text-orange-800" :
                        "bg-slate-200 text-slate-600"
                      }`}>
                        {entry.rank}
                      </div>
                      <Avatar>
                        <AvatarFallback>{entry.name?.charAt(0) || "?"}</AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-semibold">{entry.name}</p>
                        <p className="text-sm text-muted-foreground">{entry.level}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-bold text-lg">{entry.points}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Award className="w-3 h-3" />{entry.badges_count}
                        <Flame className="w-3 h-3 ml-2" />{entry.streak}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Progress Tab */}
        <TabsContent value="progress">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="w-5 h-5" /> Achievement Progress
              </CardTitle>
              <CardDescription>Track your progress towards upcoming achievements</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {stats?.achievements_progress?.map((achievement) => {
                  const badge = badges.unearned?.find(b => b.id === achievement.badge_id);
                  const IconComponent = badge ? (BADGE_ICONS[badge.icon] || Trophy) : Trophy;
                  const progressPercent = Math.min((achievement.progress / achievement.target) * 100, 100);
                  
                  return (
                    <div key={achievement.badge_id} className="space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center">
                            <IconComponent className="w-5 h-5 text-slate-600" />
                          </div>
                          <div>
                            <p className="font-medium">{badge?.name || achievement.badge_id}</p>
                            <p className="text-sm text-muted-foreground">{badge?.description}</p>
                          </div>
                        </div>
                        <Badge variant="outline">
                          {achievement.progress}/{achievement.target}
                        </Badge>
                      </div>
                      <Progress value={progressPercent} className="h-2" />
                    </div>
                  );
                })}

                {(!stats?.achievements_progress || stats.achievements_progress.length === 0) && (
                  <p className="text-center text-muted-foreground py-8">
                    All achievements unlocked! You're a champion!
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
